import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { Button, Modal } from "semantic-ui-react";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { hasWriteAccess, isAdmin } from "../../utils/utils";
import { editActions } from "../../actions/editActions";
import NumberField from "../../components/Editing/NumberField";
import DropdownField from "../../components/Editing/DropdownField";
import { DropdownOption } from "../../utils/pdApiUtils";
import ActionMenuButton from "../../components/ActionMenuButton/ActionMenuButton";
import EventTypeSections from "../Lift/EventEnumerations/EventTypeSections/EventTypeSections";

import add from "../../images/circle-plus.svg";
import close from "../../images/crossGray.svg";
import CheckboxField from "../../components/Editing/CheckboxField";

type OwnProps = {
  toggleModal: () => void;
  dataFieldName: keyof State.PdApi;
};

const EditMessageType: React.FC<OwnProps> = ({ toggleModal, dataFieldName }) => {
  const dispatch = useDispatch();
  const boundEditActions = bindActionCreators(editActions, dispatch);

  const editedEnumeration: Api.MessageType = useSelector((state: State.Root) => state.editView.editedObject);
  const storedEnumeration: Api.MessageType = useSelector((state: State.Root) => state.editView.storedObject);
  const pdApi: State.PdApi = useSelector((state: State.Root) => state.pdApi);
  const activeUser: Api.User | undefined = useSelector((state: State.Root) => state.main.activeUser);

  const [isEventTypeModalOpen, setIsEventTypeModalOpen] = React.useState(false);
  const [selectedModalEventType, setSelectedModalEventType] = React.useState<Api.BaseEnumeration | null>(null);
  const [isNonAdminDeleteModalOpen, setIsNonAdminDeleteModalOpen] = React.useState(false);

  const isNew = !storedEnumeration;
  const permission = hasWriteAccess(activeUser, "Lift");

  if (storedEnumeration) {
    storedEnumeration.comment = storedEnumeration.comment ? storedEnumeration.comment : "";
  }

  // Build event class dropdown options
  const eventClassOptions: DropdownOption[] = pdApi.eventClasses
    .sort((a, b) => a.enumValue - b.enumValue)
    .map((ec: Api.BaseEnumeration) => ({
      key: ec.enumValue,
      text: ec.enumName + " [" + ec.enumValue + "]",
      value: ec.enumValue
    }));

  const selectedEventTypes = editedEnumeration?.mapping?.eventTypes ?? [];

  const availableEventTypes = _.differenceBy(pdApi.eventTypes, selectedEventTypes, "enumValue");

  const toggleEventTypeModal = () => {
    setIsEventTypeModalOpen(prev => !prev);
    setSelectedModalEventType(null);
  };

  const isEmptyString = (str: string) => !(!!str && str.replace(/\s/g, "").length > 0);
  const checkSpecialCharacters = (str: string) => !/^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_]+$/.test(str);

  const isFieldValid = (field: string): { errorStatus: boolean; errorMsg: string } => {
    switch (field) {
      case "enumName": {
        const { enumName } = editedEnumeration;
        if (isEmptyString(enumName)) {
          return { errorStatus: true, errorMsg: "The enumeration name cannot be empty." };
        } else if (checkSpecialCharacters(enumName)) {
          return { errorStatus: true, errorMsg: "The enumeration name must contain only letters, numbers and underscores." };
        } else {
          return _.values(pdApi[dataFieldName]).find(
            (messageType: Api.MessageType) =>
              (isNew || messageType.enumValue !== editedEnumeration.enumValue) &&
              messageType.enumName.trim().toLowerCase() === editedEnumeration.enumName.trim().toLowerCase()
          )
            ? { errorStatus: true, errorMsg: "The enumeration name must be unique." }
            : { errorStatus: false, errorMsg: "" };
        }
      }
      case "enumValue":
        if (storedEnumeration) return { errorStatus: false, errorMsg: "" };
        return _.values(pdApi[dataFieldName]).find(
          (messageType: Api.MessageType) => messageType.enumValue === editedEnumeration.enumValue
        )
          ? { errorStatus: true, errorMsg: "The value must be unique." }
          : { errorStatus: false, errorMsg: "" };
      case "eventClass":
        return !editedEnumeration.mapping?.eventClass
          ? { errorStatus: true, errorMsg: "Event class must be selected." }
          : { errorStatus: false, errorMsg: "" };
      case "eventTypes":
        return !editedEnumeration.mapping?.eventTypes || editedEnumeration.mapping.eventTypes.length === 0
          ? { errorStatus: true, errorMsg: "At least one event type must be selected." }
          : { errorStatus: false, errorMsg: "" };
      default:
        return { errorStatus: false, errorMsg: "" };
    }
  };

  const isAllFieldsValid = () => {
    const fields = ["enumName", "enumValue"];
    if (editedEnumeration.mappingNeeded) {
      fields.push("eventClass", "eventTypes");
    }
    return fields.every(f => !isFieldValid(f).errorStatus);
  };

  const errorEnumName = isFieldValid("enumName");
  const errorEnumValue = isFieldValid("enumValue");
  const errorEventClass = editedEnumeration.mappingNeeded ? isFieldValid("eventClass") : { errorStatus: false, errorMsg: "" };
  const errorEventTypes = editedEnumeration.mappingNeeded ? isFieldValid("eventTypes") : { errorStatus: false, errorMsg: "" };

  const prepareAndExecute = (action: (field: Api.OptionsGrud) => void) => {
    if (!editedEnumeration.mappingNeeded) {
      boundEditActions.setEdited({ ...editedEnumeration, mapping: null });
    }
    action(dataFieldName as Api.OptionsGrud);
  };

  return (
    <Editor
      itemName={"Message Type"}
      originalObject={storedEnumeration}
      editedObject={editedEnumeration}
      hasWritePermission={permission}
      isValid={isAllFieldsValid()}
      cancel={boundEditActions.cancelEditing}
      update={() => prepareAndExecute(boundEditActions.update)}
      create={() => prepareAndExecute(boundEditActions.add)}
      remove={
        isNew
          ? undefined
          : {
              callback: isAdmin(activeUser)
                ? toggleModal
                : () => setIsNonAdminDeleteModalOpen(true)
            }
      }
    >
      <NumberField
        id="enumValue"
        label="Value"
        value={editedEnumeration ? editedEnumeration.enumValue : 0}
        isError={errorEnumValue.errorStatus}
        errorMessage={errorEnumValue.errorMsg}
        readonly={!isNew}
        onChange={enumValue =>
          boundEditActions.setEdited({ ...editedEnumeration, enumValue })
        }
      />
      <TextField
        id="enumName"
        label="Enumeration Name"
        value={editedEnumeration ? editedEnumeration.enumName : ""}
        isError={errorEnumName.errorStatus}
        errorMessage={errorEnumName.errorMsg}
        readonly={!permission}
        onChange={enumName =>
          boundEditActions.setEdited({ ...editedEnumeration, enumName: enumName.trim() })
        }
      />
      <TextField
        id="comment"
        label="Comment"
        value={editedEnumeration ? editedEnumeration.comment : ""}
        readonly={!permission}
        onChange={comment =>
          boundEditActions.setEdited({ ...editedEnumeration, comment: comment.trim() })
        }
      />
      <CheckboxField
        id="mappingneeded"
        label="Event Class & Type Mapping Available"
        checked={editedEnumeration.mappingNeeded}
        onChange={(value: boolean) =>
          boundEditActions.setEdited({
            ...editedEnumeration,
            mappingNeeded: value
          })
        }
        disabled={!permission}
      />
      {editedEnumeration.mappingNeeded && (
        <>
          <DropdownField
        id="eventClass"
        label="Event Class"
        placeholder="Select Event Class"
        value={editedEnumeration?.mapping?.eventClass ? editedEnumeration.mapping.eventClass.enumValue : null}
        options={eventClassOptions}
        isError={errorEventClass.errorStatus}
        errorMessage={errorEventClass.errorMsg}
        disabled={!permission}
        search={true}
        onChange={(value: number) => {
          const selectedClass = pdApi.eventClasses.find(ec => ec.enumValue === value) || null;
          boundEditActions.setEdited({
            ...editedEnumeration,
            mapping: { ...(editedEnumeration.mapping ?? { eventTypes: [] }), eventClass: selectedClass }
          });
        }}
      />

      <div className="add-data-sections-wrapper" style={{ marginTop: "0.5em" }}>
        <div className="field" style={{ marginBottom: 0, display: "flex", alignItems: "center", gap: "8px" }}>
          <label style={{ margin: 0 }}>Event Types</label>
          {permission && (
            <ActionMenuButton id="AddDataSection" onClick={toggleEventTypeModal}>
              <div id="addDataSectionIcon">
                <img src={add} alt="Add event type" />
                <span>Add Event Type</span>
              </div>
            </ActionMenuButton>
          )}
        </div>
        {selectedEventTypes.length > 0 && (
          <EventTypeSections
            isRowSelected={() => false}
            handleRowSelection={() => null}
            rows={selectedEventTypes}
            filterable={true}
            columnType={permission ? "Message Type Event Types With Remove" : "Message Type Event Types"}
          />
        )}
      </div>
      {errorEventTypes.errorStatus && (
        <div style={{ color: "#9f3a38", marginTop: "4px", marginBottom: "10px" }}>
          {errorEventTypes.errorMsg}
        </div>
      )}
        </>
      )}
      <Modal size="large" open={isEventTypeModalOpen} className="data-sections-modal event-type-modal" style={{ width: "560px" }}>
        <div className="modal-header">
          <p className="title">Select Event Type to add</p>
          <img
            id="closeModal"
            width="18"
            height="18"
            className="close-icon"
            src={close}
            alt="Close Icon"
            onClick={toggleEventTypeModal}
          />
        </div>
        <Modal.Content scrolling={true}>
          <EventTypeSections
            filterable={true}
            tableHeight="calc(80vh - 14rem)"
            isRowSelected={(row: Api.BaseEnumeration) =>
              selectedModalEventType?.enumValue === row.enumValue
            }
            handleRowSelection={(item: Api.BaseEnumeration) => {
              setSelectedModalEventType(item);
              boundEditActions.setEdited({
                ...editedEnumeration,
                mapping: {
                  ...(editedEnumeration.mapping ?? { eventClass: null }),
                  eventTypes: [...(editedEnumeration.mapping?.eventTypes ?? []), item]
                }
              });
            }}
            rows={availableEventTypes.sort((a, b) => a.enumValue - b.enumValue)}
            columnType="Message Type Event Types"
          />
        </Modal.Content>
      </Modal>

      <Modal size="mini" open={isNonAdminDeleteModalOpen}>
        <Modal.Header>Delete not allowed</Modal.Header>
        <Modal.Content>
          <p>Kindly contact Admin to delete this Message Type.</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setIsNonAdminDeleteModalOpen(false)}>OK</Button>
        </Modal.Actions>
      </Modal>
    </Editor>
  );
};

export default EditMessageType;

